import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {createUUId} from "../../../../../../core/services/utils/utils";
import Api from "../../../../../../core/services/api/api";
import ToastService from "../../../../../../core/services/toast";
import {Alert, alertClasses, Box, Button as MuiButton, DialogActions, DialogContent, DialogTitle, Divider, LinearProgress, Tooltip, Typography,} from "@mui/material";
import ImportingLottieAnimation from "../../../../app-specific/lottie-animations/importing";
import ReasonNoteSection from "../../../modify-product-availability/sections/reason-note-section";
import Button from "../../../../base/button/button";
import {importAdjustmentDialogDataGridColumnNames} from "../../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes, DataGridDensities} from "../../../../../../packages/data-grid";
import ProductInformationPopover from "../../../../app-specific/draggable-popover/product-information-popover";
import Chips, {ChipsTypes} from "../../../../base/chips/chips";
import PartnerDataGrid from "../../../../../containers/data-grid";
import TopBarProgressIndicator from "../../../../app-specific/topbar-progress-indicator";
import SummaryInfoCard from "../../../../app-specific/summary-cards/summary-info-card";
import DateUtils from "../../../../../../core/services/date-utils";
import {useOperatorInformation} from "../../../../../hooks/use-operator-information";
import PreventativeCorrectionDialog from "../../components/preventative-correction-dialog";
import {adjustmentImportPreventativeSections} from "../../engine/preventative-sections";
import {validateAdjustmentImport} from "../../engine/validate-adjustment-import";
import {toDistinctSerials} from "../../../../../views/panel/operator/settings/import/engine/transformers/shared/inventory-availability-validation";

const processTabs = {
    additions: 'Additions Adjustment',
    removals: 'Removals Adjustment',
    finalize: 'Finalize Adjustments',
    invalids: 'Invalid Entries',
}

/** Stub data type so the dialog can still receive selectedDataType (sections come from sectionConfigs). */
const adjustmentImportDataType = {id: 'inventory-adjustment-import', title: 'Inventory Adjustment Import'};

/** Stable empty list so preventative resetSession does not wipe patches on every re-render. */
const EMPTY_SKIPPED_FIELD_CELLS = [];

/**
 * Picks the first visible wizard tab given current review lists.
 * @param {{invalidEntries?: Array, addItems?: Array, removeItems?: Array}} reviewState
 * @returns {string}
 */
const resolveInitialStep = (reviewState) => {
    if (reviewState.invalidEntries?.length) {
        return processTabs.invalids;
    }
    if (reviewState.addItems?.length) {
        return processTabs.additions;
    }
    if (reviewState.removeItems?.length) {
        return processTabs.removals;
    }
    return processTabs.finalize;
}


const ReviewEntriesSection = ({state, setState, resetState, onSuccess}) => {
    const {t} = useTranslation('dialogs', {keyPrefix: 'app-specific.import-adjustment-dialog.reviewEntries'});
    const [submissionProgress, setSubmissionProgress] = useState({
        total: 0,
        progressed: 0,
        submitting: false,
        tooltipMessage: '',
    });
    const [step, setStep] = useState(() => resolveInitialStep(state));
    const [preventativeDialogOpen, setPreventativeDialogOpen] = useState(false);
    const [revalidating, setRevalidating] = useState(false);
    const isMounted = useIsMounted();
    const operatorInformation = useOperatorInformation();

    const hasPreventativeIssues = (state.preventativeIssues?.length ?? 0) > 0;

    /** Remount review grids when re-validation replaces list contents (same length can still change cells). */
    const reviewListsRevision = useMemo(() => {
        const summarize = (items) =>
            (items ?? [])
                .map((item) =>
                    [
                        item?.partNo ?? '',
                        item?.warehouseCode ?? '',
                        item?.quantity ?? '',
                        item?.errorMessage ?? '',
                        item?.error?.partNo ? '1' : '0',
                        item?.error?.warehouseCode ? '1' : '0',
                    ].join(':'),
                )
                .join('|');
        return [
            summarize(state.invalidEntries),
            summarize(state.addItems),
            summarize(state.removeItems),
            state.preventativeIssues?.length ?? 0,
        ].join('::');
    }, [state.addItems, state.invalidEntries, state.preventativeIssues?.length, state.removeItems]);

    // eslint-disable-next-line
    const reasonNoteFormId = useMemo(() => createUUId(), [step]);

    const initialNoteAndReason = useMemo(() => {
        return step === processTabs.additions ? state.addReasonAndNote : state.removeReasonAndNote;
    }, [state.addReasonAndNote, state.removeReasonAndNote, step])

    const progressPercentage = useMemo(() => {
        return Math.min(Math.round(submissionProgress.progressed / (submissionProgress.total === 0 ? 1 : submissionProgress.total) * 100), 100);
    }, [submissionProgress.progressed, submissionProgress.total])

    const visibleSteps = useMemo(() => {
        const stepsList = [processTabs.invalids, processTabs.additions, processTabs.removals, processTabs.finalize];
        if (!state.invalidEntries.length) {
            stepsList.splice(stepsList.indexOf(processTabs.invalids), 1);
        }
        if (!state.addItems.length) {
            stepsList.splice(stepsList.indexOf(processTabs.additions), 1);
        }
        if (!state.removeItems.length) {
            stepsList.splice(stepsList.indexOf(processTabs.removals), 1);
        }
        return stepsList;
    }, [state.addItems.length, state.invalidEntries.length, state.removeItems.length]);

    /**
     * Keeps the wizard tab on a still-visible step after preventative re-validation.
     */
    useEffect(() => {
        if (!visibleSteps.includes(step)) {
            setStep(visibleSteps[0] ?? processTabs.finalize);
        }
    }, [step, visibleSteps]);

    /**
     * Determines if a ball has been passed over relative to their process of adding multi invoices
     * @param currentType {string}
     * @param type {string}
     * @return {boolean}
     */
    const passed = useCallback((currentType, type) => {
        switch (currentType) {
            case processTabs.additions:
            default:
                return type === processTabs.invalids;
            case processTabs.removals:
                return type === processTabs.additions;
            case processTabs.finalize:
                return (type === processTabs.additions || type === processTabs.removals);
        }
    }, [])

    /**
     * Resets the state of the review section after the user has confirmed the dialog.
     * @type {(function(): void)|*}
     */
    const startOver = useCallback(() => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Are you sure that you want to start over? You would have to re-import the file.
                </p>
            </div>,
            {maxWidth: 'sm'},
            (v) => v && resetState()
        );
    }, [resetState]);

    /**
     * Updates ignored row indices while the preventative dialog is open.
     * @type {(function(number[]): void)}
     */
    const onUserSkippedRowIndicesChange = useCallback((ignoredRowIndices) => {
        setState((prev) => ({
            ...prev,
            userSkippedRowIndices: [...ignoredRowIndices],
        }));
    }, [setState]);

    /**
     * Re-validates patched rows from PreventativeCorrectionDialog and refreshes review lists.
     * @type {(function({patchedRows: Array, ignoredRowIndices?: number[]}): Promise<void>)}
     */
    const onPreventativeCorrectionSubmit = useCallback(async ({
                                                                  patchedRows,
                                                                  ignoredRowIndices = [],
                                                              }) => {
        setRevalidating(true);
        try {
            const validationResult = await validateAdjustmentImport(patchedRows, {
                ignoredRowIndices,
            });
            if (!isMounted()) {
                return;
            }

            if (!validationResult) {
                ToastService.error({
                    title: "Error",
                    message: t('revalidateFailed'),
                });
                return;
            }

            setState((prev) => ({
                ...prev,
                parsedRows: validationResult.parsedRows,
                preventativeIssues: validationResult.preventativeIssues,
                addItems: validationResult.addItems,
                removeItems: validationResult.removeItems,
                invalidEntries: validationResult.invalidEntries,
                validEntries: validationResult.validEntries,
                userSkippedRowIndices: [...ignoredRowIndices],
            }));
            setPreventativeDialogOpen(false);
            setStep(resolveInitialStep(validationResult));
        } finally {
            if (isMounted()) {
                setRevalidating(false);
            }
        }
    }, [isMounted, setState, t]);

    /**
     * Prepares the items to be imported.
     * @type {function(* ,*): *[]}
     */
    const prepareItems = useCallback((itemsToBePrepared, reasonId) => {
        const items = [];
        for (const item of itemsToBePrepared) {
            const distinctSerials = toDistinctSerials(item.serialNos ?? []);
            if (distinctSerials.length) {
                items.push(...distinctSerials.map((e) => ({
                    partNumber: item.partNo,
                    partId: item.partId,
                    parentCategoryId: item.parentCategoryId,
                    parentCategoryTitle: item.parentCategoryTitle,
                    serialNumber: e,
                    cost: item.cost,
                    quantity: 1,
                    warehouseCode: item?.warehouseCode,
                    reasonId: reasonId ?? null,
                    note: null,
                })));
            } else {
                items.push({
                    partNumber: item.partNo,
                    partId: item.partId,
                    parentCategoryId: item.parentCategoryId,
                    parentCategoryTitle: item.parentCategoryTitle,
                    cost: item.cost,
                    quantity: item.quantity,
                    warehouseCode: item?.warehouseCode,
                    reasonId: reasonId ?? null,
                    note: null,
                });
            }
        }
        return items;
    }, [])

    /**
     * Prepares the Adjustment api requests.
     * @type {function(*, *, *, *): *[]}
     */
    const prepareRequests = useCallback((items, isAdd, description, reasonId) => {
        const res = [];
        const chunks = [[]];
        for (let i = 0; i < items.length; i++) {
            if (chunks[chunks.length - 1].length === 100) {
                chunks.push([]);
            }
            chunks[chunks.length - 1].push(items[i]);
        }
        for (const chunk of chunks) {
            res.push({
                items: prepareItems(chunk, reasonId),
                chunk: chunk,
                isAdd: isAdd,
                description: description,
            });
        }
        return res;
    }, [prepareItems])

    /**
     * Imports the adjustments to the inventory.
     * @type {(function(): Promise<void>)}
     */
    const importAdjustments = useCallback(async () => {
        const adjustmentRequests = [];
        if (state.addItems.length) {
            adjustmentRequests.push(...prepareRequests(
                state.addItems,
                true,
                state.addReasonAndNote.note,
                state.addReasonAndNote.reason?.id,
            ))
        }
        if (state.removeItems.length) {
            adjustmentRequests.push(...prepareRequests(
                state.removeItems,
                false,
                state.removeReasonAndNote.note,
                state.removeReasonAndNote.reason?.id,
            ))
        }
        const res = [];
        setSubmissionProgress({
            total: adjustmentRequests.length,
            progressed: 0,
            submitting: true,
            tooltipMessage: '',
        })
        for (const adjustmentRequest of adjustmentRequests) {
            const response = await Api.createInventoryAdjustment(adjustmentRequest, false);
            if (!isMounted())
                return;
            setSubmissionProgress(prevState => ({
                ...prevState,
                progressed: prevState.progressed + 1,
                tooltipMessage: `Submitting ${prevState.progressed + 1}/${prevState.total}`
            }))
            const succeeded = response?.resultFlag ?? false;
            res.push({
                chunk: adjustmentRequest.chunk,
                isAdd: adjustmentRequest.isAdd,
                resultFlag: succeeded,
                errorMessage: succeeded ? null : (response?.message ?? t('unknownError')),
                data: succeeded
                    ? {
                        isAdd: adjustmentRequest.isAdd,
                        ...response.data,
                        submittedBy: operatorInformation?.name ?? "",
                    }
                    : null,
            });
        }
        if (!isMounted())
            return;
        setSubmissionProgress({
            total: 0,
            progressed: 0,
            submitting: false,
            tooltipMessage: '',
        })
        if (!!res.length) {
            onSuccess(res);
        } else if (adjustmentRequests.length === 0) {
            // No adjustments to submit
            onSuccess([]);
        }
    }, [state.addItems, state.removeItems, state.addReasonAndNote.note, state.addReasonAndNote.reason?.id, state.removeReasonAndNote.note, state.removeReasonAndNote.reason?.id, isMounted, prepareRequests, operatorInformation?.name, onSuccess, t])

    /**
     * Sets the reason and note of the additions' adjustment.
     */
    const setAddReasonAndNote = useCallback((reasonAndNote) => {
        setState(prevState => ({
            ...prevState,
            addReasonAndNote: typeof reasonAndNote === 'function' ? reasonAndNote(prevState.addReasonAndNote) : reasonAndNote,
        }))
    }, [setState]);

    /**
     * Sets the reason and note of the removal adjustment.
     */
    const setRemoveReasonAndNote = useCallback((reasonAndNote) => {
        setState(prevState => ({
            ...prevState,
            removeReasonAndNote: typeof reasonAndNote === 'function' ? reasonAndNote(prevState.removeReasonAndNote) : reasonAndNote,
        }))
    }, [setState]);


    /**
     * Submits the reason and note of the adjustment
     * @type {(function(*): void)}
     */
    const submitReasonAndNote = useCallback((values) => {
        if (step === processTabs.additions) {
            setAddReasonAndNote(values);
        } else if (step === processTabs.removals) {
            setRemoveReasonAndNote(values);
        }
    }, [step, setAddReasonAndNote, setRemoveReasonAndNote])

    const finalizedSummaryCardInfo = useMemo(() => ({
        header: {
            title: 'Summary',
            value: 'Adjustments Summary',
            loadingValue: "Loading...",
        },
        body: [
            {
                id: 'total',
                title: 'Total Records',
                value: state.addItems.length + state.removeItems.length + state.invalidEntries.length,
            },
            {
                id: 'invalids',
                title: 'invalid Entries',
                value: state.invalidEntries?.length ?? 0,
            },
            {
                id: 'additions',
                title: 'Addition Records',
                value: state.addItems.length,
            },
            {
                id: 'removals',
                title: 'Removal Records',
                value: state.removeItems.length,
            },
            {
                id: 'totalAdjustments',
                title: 'Total Adjustment Records',
                value: state.addItems.length + state.removeItems.length,
            },
            {
                id: 'date',
                title: 'Date',
                value: DateUtils.serverToLocal().format(DateUtils.dateFormat),
            },
            {
                id: 'submittedBy',
                title: 'Submitted By',
                value: operatorInformation?.name ?? "",
            },
        ],
    }), [operatorInformation?.name, state.addItems?.length, state.invalidEntries?.length, state.removeItems?.length])

    /**
     * Navigates back to the previous step
     * @type {(function(): void)}
     */
    const back = useCallback(() => {
        if (step === processTabs.invalids) {
            startOver();
        } else if (step === processTabs.additions) {
            if (state.invalidEntries.length) {
                setStep(processTabs.invalids);
            } else {
                startOver();
            }
        } else if (step === processTabs.removals) {
            if (state.addItems.length) {
                setStep(processTabs.additions);
            } else if (state.invalidEntries.length) {
                setStep(processTabs.invalids);
            } else {
                startOver();
            }
        } else if (step === processTabs.finalize) {
            if (state.removeItems.length) {
                setStep(processTabs.removals);
            } else if (state.addItems.length) {
                setStep(processTabs.additions);
            } else if (state.invalidEntries.length) {
                setStep(processTabs.invalids);
            } else {
                startOver();
            }
        }
    }, [startOver, state.addItems.length, state.invalidEntries.length, state.removeItems.length, step])

    /**
     * Navigates forward to the next step
     * @type {(function(): void)}
     */
    const forward = useCallback(() => {
        if (step === processTabs.invalids) {
            if (state.addItems.length) {
                setStep(processTabs.additions);
            } else if (state.removeItems.length) {
                setStep(processTabs.removals);
            } else {
                setStep(processTabs.finalize);
            }
        }
        if (step === processTabs.additions) {
            if (state.removeItems.length) {
                setStep(processTabs.removals);
            } else {
                setStep(processTabs.finalize);
            }
        } else if (step === processTabs.removals) {
            setStep(processTabs.finalize);
        } else if (step === processTabs.finalize) {
            void importAdjustments();
        }
    }, [importAdjustments, state.addItems.length, state.removeItems.length, step])

    return (
        <>
            <DialogTitle>
                <TopBarProgressIndicator
                    currentType={step}
                    types={visibleSteps}
                    passed={passed}
                />
            </DialogTitle>
            {
                (submissionProgress.submitting || revalidating) &&
                <DialogContent className={'p-3 d-flex flex-column w-100 '} sx={{gap: 1}}>
                    <ImportingLottieAnimation width={'50%'}/>
                    {
                        submissionProgress.submitting &&
                        <Tooltip
                            placement={'top'}
                            arrow
                            title={`Now submitting ${submissionProgress.tooltipMessage}`}
                        >
                            <div className={'d-flex flex-column w-100 py-3 align-items-center'}>
                                <LinearProgress
                                    className={'w-80'}
                                    variant={'buffer'}
                                    color={'secondary'}
                                    value={progressPercentage}
                                    valueBuffer={progressPercentage}
                                />
                                <p className={'identifier'}>{`${progressPercentage}%`}</p>
                            </div>
                        </Tooltip>
                    }
                    <Typography variant={'body1'} fontWeight={600} textAlign={'center'} marginBlockEnd={5}>
                        {
                            revalidating
                                ? t('revalidating')
                                : 'We are submitting your adjustments. Please do not close this dialog.'
                        }
                    </Typography>
                </DialogContent>
            }
            {
                !(submissionProgress.submitting || revalidating) &&
                <>
                    <DialogContent className={'d-flex flex-column w-100 px-4 mt--4'} sx={{gap: 0.5, paddingTop: `0 !important`}}>
                        {
                            hasPreventativeIssues &&
                            <Alert
                                severity="error"
                                variant="filled"
                                sx={{
                                    maxWidth: 900,
                                    width: '100%',
                                    marginInline: 'auto',
                                    marginBottom: 1.5,
                                    [`.${alertClasses.message}`]: {
                                        flexShrink: 1,
                                    },
                                    [`.${alertClasses.action}`]: {
                                        flexShrink: 0,
                                    },
                                }}
                                action={
                                    <MuiButton
                                        color="inherit"
                                        size="small"
                                        onClick={() => setPreventativeDialogOpen(true)}
                                        sx={{fontWeight: 'bold', textTransform: 'none'}}
                                    >
                                        {t('resolveIssues')}
                                    </MuiButton>
                                }
                            >
                                <Typography variant="subtitle2" sx={{fontWeight: 'bold'}}>
                                    {t('preventativeIssuesTitle')}
                                </Typography>
                                <Typography variant="body2" sx={{opacity: 0.9}}>
                                    {t('preventativeIssuesDescription', {count: state.preventativeIssues.length})}
                                </Typography>
                            </Alert>
                        }
                        {
                            (step === processTabs.additions || step === processTabs.removals) &&
                            <>
                                <Typography variant={'body1'} fontWeight={500} className={'px-2'}>
                                    {
                                        step === processTabs.additions ? "Additions to Inventory" : "Subtractions from the Inventory"
                                    }
                                </Typography>
                                <Typography variant={'body2'} className={'px-2'}>
                                    You can add a reason and a note for the adjustment as well.
                                    This information will be visible in the adjustment details later on for reference.
                                    Ensure that the information is accurate before proceeding.
                                </Typography>
                            </>
                        }
                        {
                            step === processTabs.invalids &&
                            <InvalidsSectionTab key={`invalids-${reviewListsRevision}`} items={state.invalidEntries}/>
                        }
                        {
                            step === processTabs.additions &&
                            <ReviewSectionTab key={`additions-${reviewListsRevision}`} items={state.addItems}/>
                        }
                        {
                            step === processTabs.removals &&
                            <ReviewSectionTab key={`removals-${reviewListsRevision}`} items={state.removeItems}/>
                        }
                        {
                            (step === processTabs.additions || step === processTabs.removals) &&
                            <ReasonNoteSection
                                formId={reasonNoteFormId}
                                onSubmit={submitReasonAndNote}
                                onUpdate={submitReasonAndNote}
                                initialValues={initialNoteAndReason}
                            />
                        }
                        {
                            step === processTabs.finalize &&
                            <Box className={'d-flex flex-column w-100 px-2'}>
                                <SummaryInfoCard
                                    header={finalizedSummaryCardInfo.header}
                                    body={finalizedSummaryCardInfo.body}
                                    className={'background-default mb-4'}
                                />
                            </Box>
                        }
                    </DialogContent>
                    <DialogActions className={'p-0 d-flex flex-column w-100'}>
                        <Divider flexItem orientation={'horizontal'}/>
                        <Box className={'d-flex align-items-center justify-content-between w-100 m-0'}>
                            <Button
                                variant={'dialog'}
                                color={'primary'}
                                onClick={back}
                                borderRadius={'0 8px 0 8px'}
                            >
                                {
                                    step === processTabs.invalids
                                        ? 'Start Over'
                                        : step === processTabs.additions && !state.invalidEntries.length
                                            ? 'Start Over'
                                            : step === processTabs.removals && !state.addItems.length && !state.invalidEntries.length
                                                ? "Start Over"
                                                : "Back"
                                }
                            </Button>
                            <Button
                                variant={'dialog'}
                                color={'primary'}
                                onClick={forward}
                                borderRadius={'8px 0 8px 0'}
                            >
                                {
                                    step === processTabs.finalize
                                        ? 'Import Adjustments'
                                        : 'Next'
                                }
                            </Button>
                        </Box>
                    </DialogActions>
                </>
            }
            <PreventativeCorrectionDialog
                open={preventativeDialogOpen}
                onClose={() => setPreventativeDialogOpen(false)}
                parsedRows={state.parsedRows ?? []}
                preventativeIssues={state.preventativeIssues ?? []}
                selectedDataType={adjustmentImportDataType}
                sectionConfigs={adjustmentImportPreventativeSections}
                userSkippedRowIndices={state.userSkippedRowIndices ?? []}
                onUserSkippedRowIndicesChange={onUserSkippedRowIndicesChange}
                userSkippedFieldCells={EMPTY_SKIPPED_FIELD_CELLS}
                onSubmit={onPreventativeCorrectionSubmit}
            />
        </>
    );
}

const ReviewSectionTab = ({items}) => {

    /**
     * Memo version of columns entities used in data grid
     * @type {DataGridColumn[]}
     */
    const dataGridColumns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: 'Warehouse',
                name: importAdjustmentDialogDataGridColumnNames.warehouse,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                width: 120,
                sortable: false,
            },
            {
                title: "Part Number",
                name: importAdjustmentDialogDataGridColumnNames.partNumber,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 150,
                sortable: false,
            },
            {
                title: "Category",
                name: importAdjustmentDialogDataGridColumnNames.category,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: 'Quantity',
                name: importAdjustmentDialogDataGridColumnNames.quantity,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                width: 60,
                sortable: false,
            },
            {
                title: 'Cost',
                name: importAdjustmentDialogDataGridColumnNames.cost,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 60,
                sortable: false,
            },
            {
                title: 'Extended Cost',
                name: importAdjustmentDialogDataGridColumnNames.extendedCost,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 100,
                sortable: false,
            },
            {
                title: 'Serial Numbers',
                name: importAdjustmentDialogDataGridColumnNames.serialNos,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                width: 200,
                sortable: false,
            },
        ], [])
    /**
     * Memo version of data grid Rows
     * @type {DataGridRow[]}
     */
    const dataGridRows = useMemo(() => {
        return items?.map((item) => ({
            key: item.id,
            data: item,
            cells: {
                [importAdjustmentDialogDataGridColumnNames.partNumber]:
                    <ProductInformationPopover partNo={item?.partNo}>
                        {item?.partNo ?? '--'}
                    </ProductInformationPopover>,
                [importAdjustmentDialogDataGridColumnNames.category]: item?.parentCategoryTitle,
                [importAdjustmentDialogDataGridColumnNames.quantity]: item?.quantity,
                [importAdjustmentDialogDataGridColumnNames.cost]: item?.cost,
                [importAdjustmentDialogDataGridColumnNames.extendedCost]: item?.quantity * (Number.isNaN(Number(item?.cost)) ? 0 : Number(item?.cost)),
                [importAdjustmentDialogDataGridColumnNames.warehouse]: item?.warehouseCode,
                [importAdjustmentDialogDataGridColumnNames.serialNos]:
                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                        {
                            item.serialNos?.map(serialNumber => (
                                <Chips
                                    key={serialNumber}
                                    type={ChipsTypes.none}
                                    content={serialNumber}
                                />
                            ))
                        }
                    </Box>,
            }
        }))
    }, [items])

    return (
        <>
            <PartnerDataGrid
                hideFooter
                hideToolbar
                initialLayoutState={{columns: dataGridColumns}}
                columns={dataGridColumns}
                rows={dataGridRows}
                visibleRows={10}
                state={{
                    pagination: {currentPage: 1, pageSize: items.length},
                    density: DataGridDensities.compact,
                }}
            />
        </>
    );
}

const InvalidsSectionTab = ({items}) => {

    const dataGridColumns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: 'Warehouse',
                name: importAdjustmentDialogDataGridColumnNames.warehouse,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                width: 120,
                sortable: false,
            },
            {
                title: "Part Number",
                name: importAdjustmentDialogDataGridColumnNames.partNumber,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 150,
                sortable: false,
            },
            {
                title: "Category",
                name: importAdjustmentDialogDataGridColumnNames.category,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: 'Quantity',
                name: importAdjustmentDialogDataGridColumnNames.quantity,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                width: 60,
                sortable: false,
            },
            {
                title: 'Cost',
                name: importAdjustmentDialogDataGridColumnNames.cost,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 60,
                sortable: false,
            },
            {
                title: 'Extended Cost',
                name: importAdjustmentDialogDataGridColumnNames.extendedCost,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 100,
                sortable: false,
            },
            {
                title: 'Serial Numbers',
                name: importAdjustmentDialogDataGridColumnNames.serialNos,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                width: 200,
                sortable: false,
            },
            {
                title: 'Error Message',
                name: importAdjustmentDialogDataGridColumnNames.errorMessage,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 200,
                sortable: false,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.right,
            }
        ], [])

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return items?.map((item) => ({
            key: item.id,
            data: item,
            cells: {
                [importAdjustmentDialogDataGridColumnNames.partNumber]:
                    item.error?.partNo === true
                        ? <Tooltip title={"Invalid Part No"}>
                            <Typography color={'error'}>{item?.partNo ?? '--'}</Typography>
                        </Tooltip>
                        : <ProductInformationPopover partNo={item?.partNo}>
                            {item?.partNo ?? '--'}
                        </ProductInformationPopover>,
                [importAdjustmentDialogDataGridColumnNames.category]: item?.parentCategoryTitle,
                [importAdjustmentDialogDataGridColumnNames.quantity]: item?.quantity,
                [importAdjustmentDialogDataGridColumnNames.cost]: item?.cost,
                [importAdjustmentDialogDataGridColumnNames.extendedCost]: item?.cost === null ? null : item?.quantity * (item?.cost),
                [importAdjustmentDialogDataGridColumnNames.warehouse]:
                    item?.error?.warehouseCode === true
                        ? <Tooltip title={"Invalid/Missing Warehouse Code"}>
                            <Typography color={'error'}>{item?.warehouseCode ?? '--'}</Typography>
                        </Tooltip>
                        : item?.warehouseCode,
                [importAdjustmentDialogDataGridColumnNames.serialNos]:
                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                        {
                            item.serialNos?.map(serialNumber => (
                                <Chips
                                    key={serialNumber}
                                    type={ChipsTypes.none}
                                    content={serialNumber}
                                />
                            ))
                        }
                    </Box>,
                [importAdjustmentDialogDataGridColumnNames.errorMessage]: item.errorMessage,
            }
        }))
    }, [items])

    return (
        <>
            <Typography variant={'body1'} fontWeight={500} className={'px-2'}>
                Invalid Entries
            </Typography>
            <Typography variant={'body2'} className={'px-2'}>
                Below are the entries that will not be imported due to errors. Please review these entries and correct them before proceeding.
                You can still export these entries to a CSV file for reference after the adjustments for the valid entries have been imported.
            </Typography>
            <PartnerDataGrid
                hideFooter
                hideToolbar
                initialLayoutState={{columns: dataGridColumns}}
                columns={dataGridColumns}
                rows={dataGridRows}
                visibleRows={10}
                state={{
                    pagination: {currentPage: 1, pageSize: items.length},
                    density: DataGridDensities.compact,
                }}
            />
        </>
    );
}

export default ReviewEntriesSection;